/**
 * Page layout primitives — roadmap 4.2.
 *
 * Four shapes cover every screen in the build: a page header with a title and
 * actions, a section with a heading, a card, and a toolbar. Defining them once
 * is what stops the instructor portal and the ops console looking like two
 * products, and it is the reason Phase 7 can replicate three more portals
 * cheaply.
 */
import { cn } from './cn'

// ---------------------------------------------------------------------------
export type PageHeaderProps = {
  title: string
  description?: string | undefined
  /** Buttons. They sit at the inline end, which mirrors in Arabic. */
  actions?: React.ReactNode
  /** Status pills and counts, under the title. */
  meta?: React.ReactNode
  className?: string | undefined
}

export function PageHeader({ title, description, actions, meta, className }: PageHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-start gap-4 pb-6', className)}>
      <div className="min-w-0 flex-1">
        {/* The brand's display serif at regular weight, as the reference sets
            it — a bold serif is heavy where a regular one is editorial. In
            Arabic `font-display` resolves to the Arabic face and
            `tracking-display` to normal spacing (see index.css), so this one
            class list is right in both languages. */}
        <h1 className="font-display text-3xl font-normal tracking-display">{title}</h1>
        {/* `max-w-prose`: the shell is fluid (Task 6), so on a 2560px screen an
            uncapped description could run 2300px before wrapping. */}
        {description ? (
          <p className="mt-1 max-w-prose text-sm text-fg-muted">{description}</p>
        ) : null}
        {meta ? <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div> : null}
      </div>
      {/* `ms-auto` — inline start, so this lands on the right in English and on
          the left in Arabic. `ml-auto` would pin it to one side in both. */}
      {actions ? <div className="ms-auto flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  )
}

// ---------------------------------------------------------------------------
export type SectionProps = React.ComponentPropsWithoutRef<'section'> & {
  title?: string | undefined
  description?: string | undefined
  actions?: React.ReactNode
}

export function Section({
  title,
  description,
  actions,
  className,
  children,
  ...props
}: SectionProps) {
  return (
    <section className={cn('space-y-4', className)} {...props}>
      {title || actions ? (
        <div className="flex flex-wrap items-baseline gap-3">
          <div className="min-w-0 flex-1">
            {title ? (
              <h2 className="font-display text-xl font-normal tracking-display">{title}</h2>
            ) : null}
            {/* `max-w-prose`: the shell is fluid (Task 6), so on a 2560px screen
                an uncapped description could run 2300px before wrapping. */}
            {description ? (
              <p className="mt-1 max-w-prose text-sm text-fg-muted">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="ms-auto flex items-center gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  )
}

// ---------------------------------------------------------------------------
export type EyebrowProps = React.ComponentPropsWithoutRef<'p'>

/**
 * The small wine label above a heading — "A COMMON THREAD FOR EGYPT'S
 * CREATIVES" over the hero title. It names what a section is *for*; the
 * heading below it says what is in it.
 *
 * ⚠️ **Never style an eyebrow by hand with `tracking-*` and `uppercase`.** The
 * `eyebrow` utility (index.css) drops both under `:lang(ar)`, because tracking
 * breaks Arabic letter joining and Arabic has no case. A hand-rolled copy gets
 * Arabic wrong the first time someone switches language.
 *
 * A `<p>`, not a heading: it is a label for the heading that follows, and as a
 * heading of its own it would add an outline level that says nothing.
 */
export function Eyebrow({ className, ...props }: EyebrowProps) {
  return <p className={cn('eyebrow', className)} {...props} />
}

// ---------------------------------------------------------------------------
export type CardProps = React.ComponentPropsWithoutRef<'div'>

/**
 * A white panel with a hairline edge — the reference's card: 7px radius, a
 * `line` border and **no shadow**. The brand separates with hairlines, not
 * elevation; a shadow under every card turns a list into a stack of tiles.
 * Menus and dialogs, which genuinely float, keep theirs.
 */
export function Card({ className, ...props }: CardProps) {
  return <div className={cn('rounded-[7px] border border-line bg-raised', className)} {...props} />
}

export function CardHeader({ className, ...props }: CardProps) {
  return <div className={cn('border-b border-line px-5 py-4', className)} {...props} />
}

export function CardBody({ className, ...props }: CardProps) {
  return <div className={cn('px-5 py-4', className)} {...props} />
}

export function CardFooter({ className, ...props }: CardProps) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 border-t border-line px-5 py-4', className)}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
/**
 * A row of controls above a table or list — search, filters, then actions.
 *
 * `flex-wrap` rather than a horizontal scroll: on a phone in Arabic a toolbar
 * that scrolls sideways hides its own filters behind the edge that the reader
 * starts from.
 */
export function Toolbar({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      role="toolbar"
      className={cn('flex flex-wrap items-center gap-2 pb-4', className)}
      {...props}
    />
  )
}
